import { Flex } from 'antd';
import { FC, useContext, useRef } from 'react';
import { type NodeProps, Position } from '@xyflow/react';
import styled from 'styled-components';
import { TextEllipsis } from '../../../../../../common/components';
import { FormField } from '../../../../../../common/form';
import { AlgorithmGraphReadOnlyField } from '../../../../AlgorithmGraphReadOnlyField';
import { GraphCard } from '../../../../GraphCard';
import { GraphCardTitle } from '../../../../GraphCardTitle';
import type { AlgorithmJoinsAndMappingsGraphNodeOfType } from '../../../schema/types';
import { AlgorithmJoinsWithMappingsGraphContext } from '../../AlgorithmJoinsWithMappingsGraphContext';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { useNodeIndex } from '../../useNodeIndex';
import { AddRemoveAlgorithmNodeButtons } from '../AddRemoveAlgorithmNodeButtons';
import { AlgorithmJoinsAndMappingsGraphHandle } from '../AlgorithmJoinsAndMappingsGraphHandle';
import { ConstantFormField } from '../ConstantFormField';
import translations from './ConstantNode.translations.json';
import { formatConstantDisplayValue } from '../formatConstantDisplayValue';

const { addTranslations, useTranslation } = i18nNsFactory('constant-node');
addTranslations(translations);

export type ConstantNodeProps = NodeProps<
  AlgorithmJoinsAndMappingsGraphNodeOfType<'constant'>
>;

export const ConstantNode: FC<ConstantNodeProps> = (props) => {
  const { id, data, selected } = props;
  const index = useNodeIndex(id);
  const name = `nodes.${index}.data.constantValue` as const;
  const { t } = useTranslation();
  const { readOnly } = useContext(AlgorithmJoinsWithMappingsGraphContext);
  const cardRef = useRef<HTMLDivElement | null>(null);

  return (
    <GraphCard
      ref={cardRef}
      size="small"
      title={
        <GraphCardTitle tagLabel={t('constant')} tagColor="green">
          {!readOnly && (
            <AddRemoveAlgorithmNodeButtons nodeId={id} nodeIndex={index} />
          )}
        </GraphCardTitle>
      }
      className={selected ? 'selected' : undefined}
    >
      {readOnly ? (
        <AlgorithmGraphReadOnlyField
          label={t('constant')}
          style={{ position: 'relative' }}
        >
          <TextEllipsis>
            {formatConstantDisplayValue(data.constantValue)}
          </TextEllipsis>
          <AlgorithmJoinsAndMappingsGraphHandle
            nodeId={id}
            type="source"
            position={Position.Right}
          />
        </AlgorithmGraphReadOnlyField>
      ) : (
        <FormField name={name} required label={t('constant-1')}>
          <ItemFieldWrapper>
            <ConstantFormField type="source" nodeId={id} name={name} />
            <AlgorithmJoinsAndMappingsGraphHandle
              nodeId={id}
              type="source"
              position={Position.Right}
            />
          </ItemFieldWrapper>
        </FormField>
      )}
    </GraphCard>
  );
};

const ItemFieldWrapper = styled(Flex)`
  position: relative;
  width: 100%;
`;
